import { createAction } from 'redux-actions';

import { CONSTANTS, ROUTES } from 'enum';
import { getCustomersList } from 'api/rest';
import { login, logout, updatePassword } from 'api/auth';
import { configOTP as configOTPCall, getUser, setUserPassword, updateUser as updateUserCall } from 'api/users';
import { extractResponseErrorStatus, createResponseErrorMessage } from 'utils/responseErrorHandler';
import history from '../../history';

import * as ACTION_TYPES from './actionTypes';
import { CLEAR_DATA } from '../common-action-types';
import { logoutUser } from '../auth/actions';

export const setUser = createAction(ACTION_TYPES.SET_USER);
export const setEmail = createAction(ACTION_TYPES.SET_EMAIL);
export const startLoading = createAction(ACTION_TYPES.START_LOADING);
export const finishLoading = createAction(ACTION_TYPES.FINISH_LOADING);
export const setError = createAction(ACTION_TYPES.SET_ERROR);
export const clearError = createAction(ACTION_TYPES.CLEAR_ERROR);
export const setPasswordError = createAction(ACTION_TYPES.SET_PASSWORD_ERROR);
export const clearPasswordError = createAction(ACTION_TYPES.CLEAR_PASSWORD_ERROR);
export const setToken = createAction(ACTION_TYPES.SET_TOKEN);
export const confirmEmail = createAction(ACTION_TYPES.CONFIRM_EMAIL);
export const setInitCompanyName = createAction(ACTION_TYPES.SET_INIT_COMPANY_NAME);
export const updateUser = createAction(ACTION_TYPES.UPDATE_USER);
export const setUserOTP = createAction(ACTION_TYPES.SET_USER_OTP);

export const clearUserData = createAction(CLEAR_DATA);

const authErrorHandlers = {
  400: 'Wrong payload',
  401: 'Token Is Required!',
  403: 'You Don`t Have Permission To Provide This Operation',
  404: 'User Not Found!',
  default: 'Error While Registering Password!',
};

const createAuthError = (error) => {
  const status = extractResponseErrorStatus(error);
  const message = createResponseErrorMessage({
    specificErrorHandler: authErrorHandlers,
    status,
  });
  const authError = new Error(message);
  authError.userMessage = message;
  return authError;
};

const registerPassword = async ({ email, password }) => {
  try {
    return await setUserPassword({ email, password });
  } catch (error) {
    throw createAuthError(error);
  }
};

const loginUser = async ({ username, password }) => {
  try {
    return await login({ username, password });
  } catch (error) {
    throw createAuthError(error);
  }
};

const getErrorPayload = (error) => error?.userMessage || error;

export const completeUser = (payload) => async (dispatch, getState) => {
  dispatch({ type: ACTION_TYPES.COMPLETE_USER, payload });
  dispatch(startLoading());

  const { firstName, lastName, password } = payload;
  const {
    auth: { isAuthenticated },
    user: { email },
  } = getState();

  try {
    if (!isAuthenticated && password) {
      await registerPassword({
        email,
        password,
      });
      const userData = await loginUser({
        username: email,
        password,
      });
      const { accessToken: token } = userData;
      if (token) {
        localStorage.setItem(CONSTANTS.LOCAL_ACCESS_TOKEN_KEY, token);
      }
    }

    const userInfo = await updateUserCall({
      firstName,
      lastName,
      email,
    });
    dispatch(setUser(userInfo));

    const customers = await getCustomersList();

    if (customers && customers.length) {
      await logout();
      dispatch(setError('Please login again to access your organization'));
      history.replace(ROUTES.LOGIN);
    } else {
      history.replace(ROUTES.ORG_INFO);
    }
  } catch (err) {
    dispatch(setError(getErrorPayload(err)));
  } finally {
    dispatch(finishLoading());
  }
};

export const resetPasswordUser = (payload) => async (dispatch, getState) => {
  dispatch({ type: ACTION_TYPES.RESET_PASSWORD, payload });
  dispatch(startLoading());

  const { password } = payload;
  const {
    user: { email: username },
  } = getState();

  try {
    await updatePassword({
      username,
      password,
    });
    dispatch(logoutUser());
    history.replace(ROUTES.LOGIN);
    dispatch(setError('Your password has been successfully reset. Please login again.'));
  } catch (error) {
    dispatch(setError(error.response?.data?.message || error.message || error));
  } finally {
    dispatch(finishLoading());
  }
};

export function updateUserFailed(error) {
  return {
    type: ACTION_TYPES.UPDATE_USER_FAILURE,
    payload: {
      message: error,
    },
  };
}

export function emailConfirmFailed(error) {
  return {
    type: ACTION_TYPES.EMAIL_CONFIRM_FAILURE,
    payload: {
      message: error,
    },
  };
}

export const getUserInfo = () => async (dispatch, getState) => {
  try {
    const email = getState().auth.username;
    const user = await getUser(email);
    dispatch(setUser(user));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }
};

export const configOTP = () => async (dispatch, getState) => {
  const { email } = getState().user;
  const response = await configOTPCall(email);
  dispatch(setUserOTP(response));
  return response;
};
